import type { EdgePosicionada } from './useGraphLayout';
import { edgeLabel, limitsText } from './accessMapVocab';

/**
 * Arestas do MESMO par (origem → destino, mesmo tipo) desenhadas como uma só.
 *
 * O servidor emite UMA aresta `tool` por capability do contrato
 * (`accessmap.go` › `EdgeTool`). Quando a identidade expõe três tools do mesmo
 * MCP, são três curvas idênticas — e três rótulos escritos no mesmo ponto médio,
 * um por cima do outro, ilegíveis (05/09). As curvas podem se sobrepor sem
 * custo; o rótulo não. Então o rótulo é um por par: a primeira tool e «+N», e
 * a lista inteira vai para o tooltip.
 *
 * Só o rótulo é agrupado. Cada aresta continua no DOM com os seus `data-*`
 * (`data-rotulo`, `data-limites`, `data-reach`, `data-axes`) e o seu `<title>`:
 * o E2E e o painel Detalhe leem por aresta, e a lente anota alcance por aresta.
 */

/** Chave do par — é por par que os rótulos se sobrepõem. */
export function pairKey(edge: EdgePosicionada): string {
  return `${edge.aresta.from}->${edge.aresta.to}:${edge.aresta.kind}`;
}

/** Agrupa preservando a ordem de chegada: a primeira aresta do par é a que rotula. */
export function groupEdgesByPair(edges: EdgePosicionada[]): Map<string, EdgePosicionada[]> {
  const groups = new Map<string, EdgePosicionada[]>();
  for (const edge of edges) {
    const key = pairKey(edge);
    const group = groups.get(key);
    if (group) group.push(edge);
    else groups.set(key, [edge]);
  }
  return groups;
}

/** «faturas.consultar +2» — a primeira tool e quantas mais. Uma só continua como era. */
export function groupLabel(group: EdgePosicionada[]): string {
  const first = edgeLabel(group[0].aresta);
  return group.length > 1 ? `${first} +${group.length - 1}` : first;
}

/**
 * Uma linha por tool, com o teto ao lado quando houver — o texto do tooltip.
 * O mesmo par (rótulo · limites) que `edgeText` escreve no `<title>`, sem o
 * diagnóstico da lente, que é por aresta e mora no `data-axes`.
 */
export function groupLines(group: EdgePosicionada[]): string[] {
  return group.map(({ aresta }) => {
    const limits = limitsText(aresta);
    return limits ? `${edgeLabel(aresta)} · ${limits}` : edgeLabel(aresta);
  });
}
